import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { GraphRendererContext } from '../model/context';
import type {
  GraphElements,
  GraphUpdate,
  GraphAppearance,
  GraphRendererAdapter,
  GraphRendererApi,
  GraphRendererHandlers,
  GraphRendererOptions,
} from '../model/types';

export type GraphRendererProps<
  Adapter extends GraphRendererAdapter<any>,
  NodeData extends Record<string, any> = Record<string, any>,
  EdgeData extends Record<string, any> = Record<string, any>,
> = GraphRendererHandlers<NodeData, EdgeData> & {
  adapter: Adapter;
  adapterOptions?: GraphRendererOptions<Adapter>;
  children?: ReactNode;
  config: {
    elements?: GraphElements<NodeData, EdgeData>;
    appearance?: GraphAppearance;
    update?: GraphUpdate;
  };
  fitOnInit?: boolean;
  fitPadding?: number;
  loading?: boolean;
  onReady?: (api: GraphRendererApi<NodeData, EdgeData>) => void;
};

export const GraphRenderer = <
  Adapter extends GraphRendererAdapter<any>,
  NodeData extends Record<string, any> = Record<string, any>,
  EdgeData extends Record<string, any> = Record<string, any>,
>({
  adapter,
  adapterOptions,
  children,
  config,
  onReady,
  ...props
}: GraphRendererProps<Adapter, NodeData, EdgeData>) => {
  const [apiState, setApiState] = useState<{
    adapterId: string;
    api: GraphRendererApi<NodeData, EdgeData>;
  } | null>(null);
  const api = apiState?.adapterId === adapter.id ? apiState.api : null;

  const handleReady = useCallback(
    (api: GraphRendererApi<NodeData, EdgeData>) => {
      setApiState({ adapterId: adapter.id, api });
      onReady?.(api);
    },
    [adapter.id, onReady],
  );

  const contextValue = useMemo(
    () => ({
      getApi: () => api as GraphRendererApi | null,
      ready: Boolean(api),
    }),
    [api],
  );

  const AdapterComponent = adapter.Component;

  return (
    <GraphRendererContext.Provider value={contextValue}>
      <AdapterComponent
        key={adapter.id}
        {...props}
        elements={config.elements}
        update={config.update}
        appearance={config.appearance}
        options={adapterOptions}
        onReady={handleReady as (api: GraphRendererApi) => void}
      >
        {children}
      </AdapterComponent>
    </GraphRendererContext.Provider>
  );
};